import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Check } from '@phosphor-icons/react';
import axios from 'axios';
import { API, CONTACT_INFO } from '../App';

const BarDetail = () => {
  const { id } = useParams();
  const [concept, setConcept] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchConcept = async () => {
      try {
        const response = await axios.get(`${API}/concepts/${id}`);
        setConcept(response.data);
      } catch (error) {
        setError('Bar non trouvé');
        console.error('Failed to fetch concept:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchConcept();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (error || !concept) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-3xl text-white mb-4">Bar non trouvé</h1>
          <Link to="/bar-mobile" className="btn-primary">
            Voir tous les bars
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black min-h-screen">
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-end">
        <div className="absolute inset-0">
          <img
            src={concept.image_url}
            alt={concept.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 pb-20">
          <Link 
            to="/bar-mobile"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft size={18} weight="light" />
            <span>Retour aux bars</span>
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white mb-3">
              {concept.title}
            </h1>
            <div className="w-12 h-px bg-white/30 mt-4 mb-3"></div>
            <p className="text-sm uppercase tracking-wider text-white/50">{concept.subtitle}</p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="section-white py-24 px-6 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            {/* Description */}
            <div className="lg:col-span-7">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="font-display text-2xl lg:text-3xl text-black mb-8">
                  À propos
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed mb-8">
                  {concept.description}
                </p>
                <p className="text-black font-medium border-l-2 border-black pl-6">
                  Pour toute demande de personnalisation, nos bars s'adaptent au type de projet avec la possibilité d'agrandir les dimensions standard.
                </p>
              </motion.div>
            </div>

            {/* Features & Dimensions */}
            <div className="lg:col-span-5 space-y-6">
              {/* Dimensions */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="bg-black p-8"
              >
                <h3 className="font-display text-xl text-white mb-6">Dimensions</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b border-white/20 pb-3">
                    <span className="text-white/70">Comptoir</span>
                    <span className="text-white font-medium">2,40 m</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-white/20 pb-3">
                    <span className="text-white/70">Backbar (L × H)</span>
                    <span className="text-white font-medium">{concept.backbar_width || "2 m"} × 2,50 m</span>
                  </div>
                </div>
                <p className="text-white/50 text-sm mt-6 italic">
                  Sur mesure : possibilité d'agrandir selon votre projet
                </p>
              </motion.div>

              {/* Features */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-gray-50 p-8"
              >
                <h3 className="font-display text-xl text-black mb-6">Caractéristiques</h3>
                <ul className="space-y-4">
                  {concept.features?.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check size={20} weight="light" className="text-black flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      {concept.gallery_images && concept.gallery_images.length > 0 && (
        <section className="bg-black py-24 px-6 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="font-display text-3xl lg:text-4xl text-white mb-4">
                Galerie
              </h2>
              <p className="text-white/50">
                Détails et accessoires
              </p>
            </motion.div>

            {/* Gallery Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {concept.gallery_images.map((image, index) => {
                const isFirst = index === 0;
                const totalImages = concept.gallery_images.length;
                const remainingAfterFirst = totalImages - 1;
                const isLast = index === totalImages - 1;
                // Make last image span remaining columns if needed to fill row
                const shouldSpanLastOnLg = isLast && remainingAfterFirst % 3 === 1;
                
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className={`overflow-hidden group ${isFirst ? 'col-span-2 lg:col-span-2 row-span-2 aspect-square' : ''} ${shouldSpanLastOnLg ? 'col-span-2 lg:col-span-2 aspect-[2/1]' : ''} ${!isFirst && !shouldSpanLastOnLg ? 'aspect-square' : ''}`}
                  >
                    <img
                      src={image}
                      alt={`${concept.title} - Détail ${index + 1}`}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

    </div>
  );
};

export default BarDetail;
